/**
 * Phase RMA-4 — RMA & Warranty Claim API helpers.
 *
 * All functions return raw response data (no axios envelope) so callers
 * can `await getRMARecord(id)` directly.
 */
import api from "./api";

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

export const getRMARecords = (params = {}) =>
  api.get("/rma-records", { params }).then((r) => r.data);

export const getRMARecord = (rmaId) =>
  api.get(`/rma-records/${rmaId}`).then((r) => r.data);

export const createRMA = (data) =>
  api.post("/rma-records", data).then((r) => r.data);

export const updateRMA = (rmaId, data) =>
  api.patch(`/rma-records/${rmaId}`, data).then((r) => r.data);

export const transitionRMAStatus = (rmaId, data) =>
  api.patch(`/rma-records/${rmaId}/status`, data).then((r) => r.data);

export const linkRMAWorkOrder = (rmaId, workOrderId) =>
  api
    .post(`/rma-records/${rmaId}/link-work-order`, {
      work_order_id: workOrderId,
    })
    .then((r) => r.data);

export const getReportRMARecords = (reportId) =>
  api.get(`/diagnostic-reports/${reportId}/rma-records`).then((r) => r.data);

// ---------------------------------------------------------------------------
// UI label + color maps
// ---------------------------------------------------------------------------

export const RMA_STATUSES = [
  "draft",
  "submitted",
  "approved",
  "denied",
  "appealing",
  "part_shipped",
  "part_received",
  "install_scheduled",
  "closed",
  "voided",
];

export const RMA_STATUS_LABEL = {
  draft: "Draft",
  submitted: "Submitted",
  approved: "Approved",
  denied: "Denied",
  appealing: "Appealing",
  part_shipped: "Part Shipped",
  part_received: "Part Received",
  install_scheduled: "Install Scheduled",
  closed: "Closed",
  voided: "Voided",
};

export const RMA_STATUS_TINT = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  submitted: "bg-blue-100 text-blue-900 border-blue-300",
  approved: "bg-emerald-100 text-emerald-900 border-emerald-300",
  denied: "bg-red-100 text-red-900 border-red-300",
  appealing: "bg-amber-100 text-amber-900 border-amber-300",
  part_shipped: "bg-purple-100 text-purple-900 border-purple-300",
  part_received: "bg-teal-100 text-teal-900 border-teal-300",
  install_scheduled: "bg-teal-200 text-teal-900 border-teal-400",
  closed: "bg-emerald-200 text-emerald-900 border-emerald-400",
  voided: "bg-slate-200 text-slate-500 border-slate-300",
};

export const RMA_VENDORS = [
  "enphase",
  "solaredge",
  "tesla",
  "eg4",
  "generac",
  "sma",
  "fronius",
  "other",
];

export const RMA_VENDOR_LABEL = {
  enphase: "Enphase",
  solaredge: "SolarEdge",
  tesla: "Tesla",
  eg4: "EG4",
  generac: "Generac",
  sma: "SMA",
  fronius: "Fronius",
  other: "Other",
};

export const RMA_COMPONENT_TYPES = [
  "inverter",
  "microinverter",
  "optimizer",
  "battery",
  "gateway",
  "panel",
  "meter",
  "combiner",
  "other",
];

export const RMA_COMPONENT_LABEL = {
  inverter: "Inverter",
  microinverter: "Microinverter",
  optimizer: "Optimizer",
  battery: "Battery",
  gateway: "Gateway",
  panel: "Panel",
  meter: "Meter",
  combiner: "Combiner",
  other: "Other",
};

// ---------------------------------------------------------------------------
// Status action map — what the sidebar action card shows for each state
// ---------------------------------------------------------------------------

/**
 * For each current status, returns the available transition actions.
 * `kind`:
 *   - "simple"           → 2-tap confirm, no extra fields
 *   - "form_denial"      → expand denial_reason textarea, then submit
 *   - "form_part_shipped"→ expand tracking_number/carrier/estimated_arrival
 *   - "form_appeal"      → expand optional note textarea, then submit
 */
export const RMA_STATUS_ACTIONS = {
  draft: [
    { to: "submitted", label: "Submit to vendor", kind: "simple", primary: true },
  ],
  submitted: [
    { to: "approved", label: "Mark approved", kind: "simple", primary: true },
    { to: "denied", label: "Mark denied", kind: "form_denial", primary: false },
  ],
  approved: [
    { to: "part_shipped", label: "Mark part shipped", kind: "form_part_shipped", primary: true },
  ],
  denied: [
    { to: "appealing", label: "Appeal denial", kind: "form_appeal", primary: true },
    { to: "voided", label: "Void RMA", kind: "simple", primary: false },
  ],
  appealing: [
    { to: "submitted", label: "Re-submit to vendor", kind: "simple", primary: true },
  ],
  part_shipped: [
    { to: "part_received", label: "Mark part received", kind: "simple", primary: true },
  ],
  part_received: [
    { to: "install_scheduled", label: "Mark install scheduled", kind: "simple", primary: true },
  ],
  install_scheduled: [
    { to: "closed", label: "Mark closed", kind: "simple", primary: true },
  ],
  closed: [],
  voided: [],
};

// ---------------------------------------------------------------------------
// Misc UI helpers
// ---------------------------------------------------------------------------

export function shortId(id, n = 6) {
  if (!id) return "";
  return id.slice(-n);
}

export function woDisplay(woId) {
  return woId ? `WO-${shortId(woId)}` : "—";
}

export function relativeTime(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  const ms = Date.now() - dt.getTime();
  if (ms < 0) return dt.toLocaleString();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

export function isoDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString();
}
